/**
 * Live snapshot hooks for the Quotations feature.
 *
 *   - `useQuotations(orgId)`     — list of every quote in the org
 *   - `useQuotation(id)`         — single-doc subscription
 *   - `useQuotationRevisions(id)` — append-only revision history
 *
 * Firestore rules already scope reads to org members. Sort is
 * `createdAt` desc client-side so docs missing the field (legacy
 * imports) sink rather than vanish.
 */
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { db } from '@/lib/firebase';
import type { Quotation, QuotationRevision } from '@/lib/quotations';

// ───── List ─────

export type UseQuotationsResult = {
  quotations: Quotation[];
  loading: boolean;
  error: string | null;
};

export function useQuotations(
  orgId: string | null | undefined,
): UseQuotationsResult {
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      setQuotations([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      query(collection(db, 'quotations'), where('orgId', '==', orgId)),
      (snap) => {
        const rows: Quotation[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Quotation, 'id'>),
        }));
        rows.sort((a, b) => {
          const at = a.createdAt?.toMillis?.() ?? 0;
          const bt = b.createdAt?.toMillis?.() ?? 0;
          return bt - at;
        });
        setQuotations(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useQuotations] snapshot error:', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [orgId]);

  return { quotations, loading, error };
}

// ───── Single doc ─────

export type UseQuotationResult = {
  quotation: Quotation | null;
  loading: boolean;
  error: string | null;
};

export function useQuotation(
  quotationId: string | null | undefined,
): UseQuotationResult {
  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!quotationId) {
      setQuotation(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      doc(db, 'quotations', quotationId),
      (snap) => {
        if (!snap.exists()) {
          setQuotation(null);
        } else {
          setQuotation({
            id: snap.id,
            ...(snap.data() as Omit<Quotation, 'id'>),
          });
        }
        setLoading(false);
      },
      (err) => {
        console.warn('[useQuotation] snapshot error:', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [quotationId]);

  return { quotation, loading, error };
}

// ───── Revision history ─────

export type UseQuotationRevisionsResult = {
  revisions: QuotationRevision[];
  loading: boolean;
};

export function useQuotationRevisions(
  quotationId: string | null | undefined,
): UseQuotationRevisionsResult {
  const [revisions, setRevisions] = useState<QuotationRevision[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!quotationId) {
      setRevisions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      collection(db, 'quotations', quotationId, 'revisions'),
      (snap) => {
        const rows: QuotationRevision[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<QuotationRevision, 'id'>),
        }));
        // Newest first (highest version number).
        rows.sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
        setRevisions(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useQuotationRevisions] snapshot error:', err);
        setLoading(false);
      },
    );
  }, [quotationId]);

  return { revisions, loading };
}
